import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { QrCode, Plus, Trash2, Download, Copy, MapPin } from "lucide-react";

export default function NamedQrCodes() {
  const [newName, setNewName] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [selectedQr, setSelectedQr] = useState<string | null>(null);

  const qrList = trpc.namedQr.list.useQuery();
  const createMutation = trpc.namedQr.create.useMutation({
    onSuccess: () => {
      qrList.refetch();
      setNewName("");
      setNewLocation("");
      setNewDescription("");
      toast.success("QR Code de ambiente criado com sucesso.");
    },
    onError: (err) => toast.error(err.message),
  });
  const deleteMutation = trpc.namedQr.delete.useMutation({
    onSuccess: () => {
      qrList.refetch();
      toast.success("QR Code removido");
    },
  });

  const qrImage = trpc.namedQr.generateQr.useQuery(
    { code: selectedQr || "", origin: window.location.origin },
    { enabled: !!selectedQr }
  );

  const handleCreate = () => {
    if (!newName.trim()) {
      toast.error("Informe um nome para o QR Code (ex: Banheiro Feminino, Portaria Norte)");
      return;
    }
    createMutation.mutate({ name: newName.trim(), location: newLocation.trim() || undefined, description: newDescription.trim() || undefined });
  };

  const handleCopyUrl = (code: string) => {
    const url = `${window.location.origin}/app?qr=${code}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copiada!");
  };

  const handleDownloadQr = () => {
    if (!qrImage.data?.qrCode) return;
    const link = document.createElement("a");
    link.href = qrImage.data.qrCode;
    link.download = `qr-${selectedQr}.png`;
    link.click();
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <QrCode className="h-8 w-8 text-violet-500" />
        <div>
          <h1 className="text-2xl font-bold">QR Codes por Ambiente</h1>
          <p className="text-sm text-muted-foreground">Gere QR Codes nomeados para identificar locais além do GPS</p>
        </div>
      </div>

      {/* Create new QR */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="h-5 w-5" /> Criar Novo QR Code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              placeholder="Nome do ambiente *"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <Input
              placeholder="Localização (ex: Bloco A, 2º andar)"
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
            />
            <Input
              placeholder="Descrição (opcional)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
          </div>
          <Button onClick={handleCreate} disabled={createMutation.isPending}>
            {createMutation.isPending ? "Criando..." : "Criar QR Code"}
          </Button>
        </CardContent>
      </Card>

      {/* QR List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {qrList.data?.map((qr) => (
          <Card key={qr.id} className={`cursor-pointer transition-all ${selectedQr === qr.code ? "ring-2 ring-violet-500" : ""}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1" onClick={() => setSelectedQr(qr.code)}>
                  <h3 className="font-semibold text-lg">{qr.name}</h3>
                  {qr.location && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {qr.location}
                    </p>
                  )}
                  {qr.description && (
                    <p className="text-xs text-muted-foreground mt-1">{qr.description}</p>
                  )}
                  <p className="text-xs font-mono text-violet-400 mt-2">Código: {qr.code}</p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCopyUrl(qr.code)}
                    title="Copiar URL"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteMutation.mutate({ id: qr.id })}
                    title="Excluir"
                  >
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {qrList.data?.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <QrCode className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Nenhum QR Code criado ainda.</p>
          <p className="text-sm">Crie QR Codes para identificar ambientes como banheiros, portarias, estacionamentos, etc.</p>
        </div>
      )}

      {/* QR Preview */}
      {selectedQr && qrImage.data && (
        <Card>
          <CardContent className="p-6 flex flex-col items-center gap-4">
            <h3 className="font-semibold text-lg">
              QR Code: {qrList.data?.find(q => q.code === selectedQr)?.name}
            </h3>
            <img src={qrImage.data.qrCode} alt="QR Code" className="w-64 h-64" />
            <p className="text-sm text-muted-foreground break-all text-center">{qrImage.data.url}</p>
            <div className="flex gap-3">
              <Button onClick={handleDownloadQr} variant="outline">
                <Download className="h-4 w-4 mr-2" /> Baixar PNG
              </Button>
              <Button onClick={() => handleCopyUrl(selectedQr)} variant="outline">
                <Copy className="h-4 w-4 mr-2" /> Copiar URL
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
